import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMsalInstance, loginRequest, getAllTokens } from "../../msalConfig";
import { loader } from "../../Utilities/loader";
import { tokenRefreshService } from "../../services/tokenRefreshService";
import {
  storeUserInfo,
  setAdminAccess,
  setSupervisorAccess,
  setSemanticAdminAccess,
  getUserId,
  extractUserIdFromEmail,
  setSessionLoginAt,
  ensureSessionLoginAt,
  isSessionExpired,
} from "../../utils/userUtils";
import { performAppLogout } from "../../utils/authLogout";
import { adminService } from "../../services/adminService";
import { supervisorService } from "../../services/supervisorService";
import { semanticService } from "../../services/semanticService";
import "./login.css";
import emcureLogo from "../../assets/images/emcure_logo.svg";
import microsoftLogo from "../../assets/images/Microsoft_Symbol_0.svg";

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const msalInstance = getMsalInstance();

  useEffect(() => {
    // // TEMPORARY BYPASS - Remove this in production
    // navigate("/chatPage", { replace: true });
    // return;
    
    const init = async () => {
      const syncAdminAccess = async (account) => {
        try {
          if (!account || !account.username) {
            setAdminAccess(false);
            return false;
          }
          const response = await adminService.checkAccess(account.username);
          const hasAccess = !!response && response.access_granted === true;
          setAdminAccess(hasAccess);
          return hasAccess;
        } catch (error) {
          setAdminAccess(false);
          return false;
        }
      };

      const syncSupervisorAccess = async (account) => {
        try {
          if (!account || !account.username) {
            setSupervisorAccess(false);
            return false;
          }
          const response = await supervisorService.checkAccess(account.username);
          const hasAccess = !!response && response.access_granted === true;
          setSupervisorAccess(hasAccess);
          return hasAccess;
        } catch (error) {
          setSupervisorAccess(false);
          return false;
        }
      };

      const syncSemanticAdminAccess = async (account) => {
        try {
          if (!account || !account.username) {
            setSemanticAdminAccess(false);
            return false;
          }
          const response = await semanticService.checkAccess(account.username);
          const hasAccess = !!response && response.access_granted === true;
          setSemanticAdminAccess(hasAccess);
          return hasAccess;
        } catch (error) {
          setSemanticAdminAccess(false);
          return false;
        }
      };

      const syncAccessFlags = async (account) => {
        // Trigger access checks concurrently so all panel gates are current.
        const [isAdmin, isSupervisor, isSemanticAdmin] = await Promise.all([
          syncAdminAccess(account),
          syncSupervisorAccess(account),
          syncSemanticAdminAccess(account),
        ]);
        return { isAdmin, isSupervisor, isSemanticAdmin };
      };

      try {
        await msalInstance.initialize();

        const response = await msalInstance.handleRedirectPromise();
        if (response && response.account) {
          msalInstance.setActiveAccount(response.account);
          
          try {
            storeUserInfo(response.account);
            const account = response.account;
            const userId =
              getUserId() ||
              extractUserIdFromEmail(account?.username) ||
              account?.localAccountId ||
              "anonymous";
            const { appToken, databricksToken, appExpiresOn, databricksExpiresOn } = await getAllTokens(response.account);
            
            if (databricksToken || appToken) {
              setSessionLoginAt();
              tokenRefreshService.storeTokens(appToken, databricksToken, null, { appExpiresOn, databricksExpiresOn });
              tokenRefreshService.startAutoRefresh();
              await syncAccessFlags(response.account);
              navigate("/departments", { replace: true });
              return;
            }
          } catch (error) {
          }
        }

        let account = msalInstance.getActiveAccount();
        if (!account) {
          const allAccounts = msalInstance.getAllAccounts();
          if (allAccounts.length > 0) {
            account = allAccounts[0];
            msalInstance.setActiveAccount(account);
          }
        }

        if (account) {
          try {
            ensureSessionLoginAt();
            if (isSessionExpired()) {
              await performAppLogout();
              setLoading(false);
              return;
            }

            storeUserInfo(account);
            const userId =
              getUserId() ||
              extractUserIdFromEmail(account?.username) ||
              account?.localAccountId ||
              "anonymous";
            const { appToken, databricksToken, appExpiresOn, databricksExpiresOn } = await getAllTokens(account);

            if (databricksToken || appToken) {
              tokenRefreshService.storeTokens(appToken, databricksToken, null, { appExpiresOn, databricksExpiresOn });
              tokenRefreshService.startAutoRefresh();
              await syncAccessFlags(account);
              navigate("/departments", { replace: true });
              return;
            }
          } catch (error) {
          }
        }
        
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    init();
  }, [navigate, msalInstance]);

  const signIn = async () => {
    try {
      await msalInstance.initialize();
      await msalInstance.loginRedirect(loginRequest);
    } catch (error) {
    }
  };

  if (loading) {
    return (
      <div id="spinner-loader">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-left">
        <div className="login-branding">
          <h1 className="product-title">Emcure Analytics Assistant</h1>
          <p className="product-tagline">AI-Powered Business Intelligence at Your Fingertips</p>
        </div>
        
        <div className="features-section">
          <div className="feature-item">
            <div className="feature-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#B90005" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </div>
            <div className="feature-content">
              <h3>Sales Analytics</h3>
              <p>Get instant insights into sales performance, trends, and forecasts</p>
            </div>
          </div>

          <div className="feature-item">
            <div className="feature-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#B90005" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div className="feature-content">
              <h3>Field Force Intelligence</h3>
              <p>Track and analyze field team performance and activities</p>
            </div>
          </div>

          <div className="feature-item">
            <div className="feature-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#B90005" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            </div>
            <div className="feature-content">
              <h3>Natural Language Queries</h3>
              <p>Ask questions in plain English and get data-driven answers</p>
            </div>
          </div>

          <div className="feature-item">
            <div className="feature-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#B90005" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <div className="feature-content">
              <h3>Interactive Visualizations</h3>
              <p>Dynamic charts and graphs that bring your data to life</p>
            </div>
          </div>
        </div>
      </div>

      <div className="login-right">
        <div className="login-card">
          <img className="login-logo" src={emcureLogo} alt="Emcure" />
          <h1 className="login-title">Welcome Back</h1>
          <p className="login-subtitle">Sign in to access your analytics dashboard</p>
          <button onClick={signIn} className="login-button">
            <img src={microsoftLogo} alt="Microsoft" className="ms-logo" />
            <span>Sign in with Microsoft</span>
          </button>
          <div className="login-footer">
            <p>Secure authentication powered by Microsoft Azure</p>
          </div>
        </div>
      </div>
    </div>
  );
}
